// Tenantra Inc. - Back office: pull, review, approve and export the monthly Equifax rental file
import { useEffect, useState } from 'react';
import { Loader2, Download, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import api, { apiHelpers } from '@/services/api';
import { RowOpenButton } from '@/components/dashboard/TableKit';
import { DetailSheet, Section } from '@/components/dashboard/DetailPanel';

const TABS = [
  { key: 'ready', label: 'Ready', cls: 'text-emerald-700' },
  { key: 'needs_fixing', label: 'Needs fixing', cls: 'text-red-600' },
  { key: 'held', label: 'Held', cls: 'text-amber-700' },
  { key: 'excluded', label: 'Excluded', cls: 'text-slate-500' },
];
const STATUS_WORDS = { 0: 'On time (0 to 30 days)', 1: '31 to 60 days late', 2: '61 to 90 days late', 3: '91 to 120 days late', 4: '121 to 150 days late', 5: '151 to 180 days late', 6: '181+ days late', 9: 'Closed, paid in full', 10: 'Closed, unpaid balance' };

const completedMonths = () => Array.from({ length: 12 }, (_, i) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1 - i);
  return { value: `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }) };
});
const monthName = (yyyymm) => new Date(+yyyymm.slice(0, 4), +yyyymm.slice(4) - 1, 1).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });

function MemberNumber({ settings, onSaved }) {
  const [val, setVal] = useState(settings.member_number || '');
  const save = async () => {
    try { const r = await api.put('/admin/equifax/settings', { member_number: val.trim() }); onSaved(r.data); toast.success('Member number saved'); }
    catch (e) { toast.error(apiHelpers.handleError(e)); }
  };
  return (
    <div className="flex items-end gap-2">
      <div>
        <label className="text-[11px] text-slate-500 block mb-1" htmlFor="eq-member">Equifax Member Number</label>
        <Input id="eq-member" value={val} onChange={(e) => setVal(e.target.value.toUpperCase())} placeholder="###AA#####" maxLength={10} className="h-9 w-40 font-mono text-sm" data-testid="equifax-member-input" />
      </div>
      <button onClick={save} disabled={val.trim().length !== 10 || val === settings.member_number} className="h-9 px-3 text-sm rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40" data-testid="equifax-member-save">Save</button>
    </div>
  );
}

function RowPanel({ row, onClose }) {
  const v = row?.values;
  return (
    <DetailSheet open={!!row} onClose={onClose} loading={false} testId="equifax-row-panel" crumb="Equifax Reporting / Record"
      title={row?.tenant_name} meta={row && <span className="text-slate-500">{row.landlord_name} · {row.property_address}</span>}>
      {row && <>
        {(row.errors.length > 0 || row.reason || row.warnings.length > 0) && (
          <Section title="Issues">
            <ul className="space-y-1 text-sm" data-testid="equifax-row-issues">
              {row.reason && <li className="text-slate-700">{row.reason}</li>}
              {row.errors.map(e => <li key={e} className="text-red-600">{e}</li>)}
              {row.warnings.map(w => <li key={w} className="text-amber-700">{w}</li>)}
            </ul>
          </Section>
        )}
        {v ? (
          <Section title="Values sent to Equifax" aside="Equifax column order">
            <dl className="divide-y divide-slate-100 border border-slate-100 rounded-md text-sm" data-testid="equifax-row-values">
              {Object.entries(v).filter(([k, val]) => val || !k.startsWith('Joint')).map(([k, val]) => (
                <div key={k} className="grid grid-cols-[1fr_1.2fr] gap-3 px-3 py-1.5">
                  <dt className="text-slate-500 text-xs">{k}</dt>
                  <dd className="text-slate-900 font-mono text-xs break-all">{val || <span className="text-slate-300">blank</span>}</dd>
                </div>
              ))}
            </dl>
          </Section>
        ) : <Section title="Values"><p className="text-sm text-slate-500">Not built for excluded records.</p></Section>}
      </>}
    </DetailSheet>
  );
}

function BatchTable({ rows, onOpen }) {
  if (!rows.length) return <p className="px-4 py-8 text-center text-sm text-slate-500">Nothing in this tab.</p>;
  return (
    <div className="divide-y divide-slate-100">
      <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
        <div className="col-span-3">Tenant</div><div className="col-span-3">Landlord / property</div><div className="col-span-1">Rent</div>
        <div className="col-span-1">Received</div><div className="col-span-3">Status / issue</div><div className="col-span-1" />
      </div>
      {rows.map(r => (
        <div key={r.id} onClick={() => onOpen(r)} className="grid grid-cols-12 gap-3 px-4 py-2.5 items-center hover:bg-slate-50 cursor-pointer" data-testid={`equifax-row-${r.tenancy_id}`}>
          <div className="col-span-10 md:col-span-3 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{r.tenant_name}</p>
            <p className="text-[11px] text-slate-400 font-mono">{r.values?.['Account Number'] || 'Not built'}</p>
          </div>
          <div className="hidden md:block col-span-3 min-w-0">
            <p className="text-xs text-slate-700 truncate">{r.landlord_name}</p>
            <p className="text-[11px] text-slate-400 truncate">{r.property_address}</p>
          </div>
          <div className="hidden md:block col-span-1 text-xs text-slate-700 tabular-nums">{r.values ? `$${r.values['Monthly Rent Amount']}` : ''}</div>
          <div className="hidden md:block col-span-1 text-xs text-slate-700 tabular-nums">{r.values ? `$${r.values['Actual Payment Received']}` : ''}</div>
          <div className="hidden md:block col-span-3 text-xs">
            {r.bucket === 'ready' ? <span className="text-slate-700">{STATUS_WORDS[r.values['Payment Status (Days late)']] || `Status ${r.values['Payment Status (Days late)']}`}{r.warnings.length ? <span className="text-amber-700"> · check name</span> : ''}</span>
              : <span className={r.errors.length ? 'text-red-600' : 'text-slate-600'}>{r.reason || r.errors[0]}</span>}
          </div>
          <div className="col-span-2 md:col-span-1 flex justify-end"><RowOpenButton onClick={() => onOpen(r)} testId={`equifax-open-${r.tenancy_id}`} /></div>
        </div>
      ))}
    </div>
  );
}

export function EquifaxReportingPanel() {
  const months = completedMonths();
  const [month, setMonth] = useState(months[0].value);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [batch, setBatch] = useState(null);
  const [tab, setTab] = useState('ready');
  const [busy, setBusy] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const loadHistory = () => api.get('/admin/equifax/batches').then(r => setHistory(r.data.items || []));
  useEffect(() => { api.get('/admin/equifax/settings').then(r => setSettings(r.data)); loadHistory(); }, []);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); } };
  const pull = () => run(async () => { const r = await api.post('/admin/equifax/pull', { month }); setBatch(r.data); setTab('ready'); loadHistory(); toast.success(`${monthName(month)} pulled`); });
  const open = (id) => run(async () => { const r = await api.get(`/admin/equifax/batches/${id}`); setBatch(r.data); setMonth(r.data.month); });
  const approve = () => run(async () => {
    setConfirming(false);
    const r = await api.post(`/admin/equifax/batches/${batch.id}/approve`); setBatch({ ...batch, ...r.data }); loadHistory(); toast.success('Batch approved and locked');
  });
  const download = () => run(async () => {
    const r = await api.get(`/admin/equifax/batches/${batch.id}/csv`, { responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = `tenantra-equifax-${batch.month}.xlsx`; a.click();
  });

  const approved = batch?.status === 'approved';
  const monthLocked = history.some(h => h.month === month && h.status === 'approved');
  const changeMonth = (m) => {
    setMonth(m);
    const existing = history.find(h => h.month === m && h.status === 'approved') || history.find(h => h.month === m);
    if (existing) open(existing.id); else setBatch(null);
  };
  return (
    <div className="space-y-5" data-testid="equifax-reporting-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Equifax Reporting</h1>
          <p className="text-sm text-slate-500">Pull the previous month for every Tenantra client, review it, approve it, then export the Equifax file.</p>
        </div>
        {settings && <MemberNumber settings={settings} onSaved={setSettings} />}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[11px] text-slate-500 block mb-1" htmlFor="eq-month">Reporting month</label>
          <select id="eq-month" value={month} onChange={(e) => changeMonth(e.target.value)} className="h-9 px-3 pr-8 border border-slate-200 rounded-md text-sm bg-white" data-testid="equifax-month-select">
            {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <button onClick={pull} disabled={busy || monthLocked} className="h-9 px-4 inline-flex items-center gap-2 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium disabled:opacity-50" data-testid="equifax-pull-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {monthLocked ? 'Month locked' : 'Pull report'}
        </button>
        <p className="text-xs text-slate-400 basis-full">Source for now: landlord monthly entries. When Zūm Rails collection is live this switches to settled PAD payments, with no change to this page.</p>
      </div>

      {settings && !settings.member_number && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="equifax-member-missing">Add your Equifax Member Number before approving. Records can still be pulled and reviewed.</p>}

      {batch && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="equifax-batch">
          <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">{monthName(batch.month)} batch
                {approved ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-medium"><Lock className="w-3 h-3" /> Approved {new Date(batch.approved_at).toLocaleDateString('en-CA')}</span>
                  : <span className="text-xs text-slate-400 font-normal">Draft · pulled {new Date(batch.pulled_at).toLocaleString('en-CA')}</span>}
              </p>
              {approved && <p className="text-[11px] text-slate-400 font-mono mt-0.5" data-testid="equifax-batch-hash">SHA-256 {batch.file_sha256}</p>}
            </div>
            <div className="flex items-center gap-2">
              {!approved && <button onClick={() => setConfirming(true)} disabled={busy || !batch.counts.ready || !batch.member_number} className="h-9 px-4 inline-flex items-center gap-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium disabled:opacity-40" data-testid="equifax-approve-btn"><CheckCircle2 className="w-4 h-4" /> Approve {batch.counts.ready} records</button>}
              {approved && <button onClick={download} disabled={busy} className="h-9 px-4 inline-flex items-center gap-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium" data-testid="equifax-download-btn"><Download className="w-4 h-4" /> Download Equifax File</button>}
            </div>
          </div>
          <div className="flex border-b border-slate-100 overflow-x-auto">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-teal-600 text-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-testid={`equifax-tab-${t.key}`}>
                {t.label} <span className={`ml-1 font-semibold ${t.cls}`}>{batch.counts[t.key] || 0}</span>
              </button>
            ))}
          </div>
          <BatchTable rows={batch.rows.filter(r => r.bucket === tab)} onOpen={setOpenRow} />
        </div>
      )}

      {history.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="equifax-history">
          <p className="px-4 py-2.5 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wide">Batch history</p>
          <ul className="divide-y divide-slate-100">
            {history.map(h => (
              <li key={h.id}><button onClick={() => open(h.id)} className="w-full px-4 py-2.5 flex items-center justify-between text-left hover:bg-slate-50" data-testid={`equifax-history-${h.month}`}>
                <span className="text-sm text-slate-800">{monthName(h.month)}</span>
                <span className="text-xs text-slate-500">{h.counts.ready} ready · {h.status === 'approved' ? <span className="text-emerald-700 font-medium">Approved</span> : 'Draft'}</span>
              </button></li>
            ))}
          </ul>
        </div>
      )}
      <RowPanel row={openRow} onClose={() => setOpenRow(null)} />
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent className="bg-white" data-testid="equifax-approve-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {batch?.counts.ready} records for {batch && monthName(batch.month)}?</AlertDialogTitle>
            <AlertDialogDescription>The month will be locked. Records in Needs fixing, Held and Excluded are not included in the file.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="equifax-approve-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={approve} className="bg-teal-600 hover:bg-teal-700" data-testid="equifax-approve-confirm">Approve and lock</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
